/** «Новая задача»: что сделать, какому чату, приоритет, подробности; задача встаёт в конец своего приоритета */
import {
  BOARD_PRIO_HINTS,
  BOARD_PRIOS,
  type BoardMember,
  type BoardPrio,
  type BoardTaskDetail,
  type BoardTaskInput,
} from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { BOARD_API } from './access';
import { useRefreshBoard } from './shared';

export function NewTaskDialog({
  open,
  onOpenChange,
  defaultChat,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultChat?: string;
  onCreated: (id: string) => void;
}) {
  const refresh = useRefreshBoard();
  const [title, setTitle] = useState('');
  const [chat, setChat] = useState(defaultChat && defaultChat !== 'none' ? defaultChat : '');
  const [prio, setPrio] = useState<BoardPrio>('П2');
  const [blocked, setBlocked] = useState(false);
  const [reqs, setReqs] = useState('');
  const [lead, setLead] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const members = useQuery({
    queryKey: ['devboard', 'members'],
    queryFn: () => api<BoardMember[]>(`${BOARD_API}/members`),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const create = useMutation({
    mutationFn: () => {
      const body: BoardTaskInput = {
        title: title.trim(),
        chat: chat.trim() || null,
        prio,
        status: blocked ? 'blocked' : 'new',
        reqs: reqs
          .split(/[,;\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        details: details.trim() || null,
        leadUserId: lead || null,
      };
      return api<BoardTaskDetail>(`${BOARD_API}/tasks`, { body });
    },
    onSuccess: (t) => {
      toast('Задача на доске');
      refresh();
      setTitle('');
      setDetails('');
      setReqs('');
      setBlocked(false);
      onOpenChange(false);
      onCreated(t.id);
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    create.mutate();
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Новая задача"
      description="Встанет в конец своего приоритета; порядок можно поменять в карточке."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="new-board-task"
            loading={create.isPending}
            disabled={!title.trim()}
          >
            Создать
          </Button>
        </>
      }
    >
      <form id="new-board-task" onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <Field label="Что сделать" htmlFor="new-title" error={errors.title}>
          <Input id="new-title" value={title} maxLength={500} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Чат" htmlFor="new-chat" hint="Код: plan, site, main, owner…" error={errors.chat}>
            <Input
              id="new-chat"
              value={chat}
              maxLength={32}
              autoCapitalize="none"
              onChange={(e) => setChat(e.target.value.toLowerCase())}
            />
          </Field>
          <Field label="Приоритет" htmlFor="new-prio" hint={BOARD_PRIO_HINTS[prio]}>
            <Select id="new-prio" value={prio} onChange={(e) => setPrio(e.target.value as BoardPrio)}>
              {BOARD_PRIOS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Ведёт со стороны компании" htmlFor="new-lead">
            <Select id="new-lead" value={lead} onChange={(e) => setLead(e.target.value)}>
              <option value="">—</option>
              {(members.data ?? []).map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Требования ТЗ" htmlFor="new-reqs" hint="Через запятую" error={errors.reqs}>
            <Input id="new-reqs" value={reqs} onChange={(e) => setReqs(e.target.value)} />
          </Field>
        </div>
        <Field label="Подробности" htmlFor="new-details">
          <Textarea
            id="new-details"
            value={details}
            maxLength={20_000}
            onChange={(e) => setDetails(e.target.value)}
          />
        </Field>
        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-[14px] text-ink md:min-h-0">
          <input
            type="checkbox"
            checked={blocked}
            onChange={(e) => setBlocked(e.target.checked)}
            className="size-4 accent-[var(--color-ink)]"
          />
          Ждёт ответа (вопрос владельцу — чат owner)
        </label>
        <ErrorBox error={Object.keys(errors).length ? null : create.error} />
      </form>
    </Dialog>
  );
}
